import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { LogOut, Plus, RefreshCw, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "gls_admin_token";
const authHeaders = (token) => ({ headers: { "X-Admin-Token": token } });

const EMPTY = {
  id: "",
  slug: "",
  title: "",
  meta_title: "",
  description: "",
  excerpt: "",
  subtitle: "",
  image: "",
  image_alt: "",
  target_keyword: "",
  body_html: "",
  canonical_url: "",
  author: "Dr. Andrew Campbell",
  category: "blog",
  published: true,
  published_at: "",
};

const toDateInput = (val) => {
  if (!val) return "";
  try {
    return new Date(val).toISOString().slice(0, 10);
  } catch { return ""; }
};

export default function AdminBlog() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async (t) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/blog/posts`, authHeaders(t || token));
      setPosts(r.data.posts || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  const login = async () => {
    if (!token.trim()) return toast.error("Enter your admin token");
    try {
      await axios.get(`${API}/admin/blog/posts`, authHeaders(token.trim()));
      localStorage.setItem(TOKEN_KEY, token.trim());
      setAuthed(true);
      load(token.trim());
    } catch {
      toast.error("Invalid admin token");
    }
  };

  useEffect(() => {
    if (token) {
      axios.get(`${API}/admin/blog/posts`, authHeaders(token))
        .then((r) => { setAuthed(true); setPosts(r.data.posts || []); })
        .catch(() => setAuthed(false));
    }
  }, []); // eslint-disable-line

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setPosts([]);
    setForm(EMPTY);
  };

  const editPost = (p) => setForm({ ...EMPTY, ...p, published_at: toDateInput(p.published_at) });
  const newPost = () => setForm(EMPTY);

  const save = async () => {
    if (!form.slug || !form.title || !form.body_html) {
      return toast.error("Slug, title, and body are required");
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.id) delete payload.id;
      if (!payload.published_at) delete payload.published_at;
      const r = await axios.post(`${API}/admin/blog/posts`, payload, authHeaders(token));
      toast.success(`Saved: ${r.data.post.slug}`);
      await load();
      setForm({ ...EMPTY, ...r.data.post });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendNewsletter = async () => {
    if (!form.id) return;
    if (!window.confirm(`Send "${form.title}" to every active subscriber? This cannot be undone.`)) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/newsletter/send`, { post_id: form.id }, authHeaders(token));
      const { sent, failed, recipients_count } = r.data;
      toast.success(`Sent ${sent}/${recipients_count} · ${failed} failed`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const remove = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/blog/posts/${post.id}`, authHeaders(token));
      toast.success("Deleted");
      await load();
      if (form.id === post.id) setForm(EMPTY);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const grouped = useMemo(() => ({
    blog: posts.filter((p) => (p.category || "blog") === "blog"),
    newsletter: posts.filter((p) => p.category === "newsletter"),
  }), [posts]);

  if (!authed) {
    return (
      <div className="max-w-md mx-auto py-24 px-6" data-testid="admin-blog-login">
        <h1 className="font-serif text-3xl text-[#2D2628] mb-6 text-center">Admin — Blog</h1>
        <input type="password" placeholder="Admin token" value={token}
          onChange={(e) => setToken(e.target.value)}
          className="w-full px-4 py-3 border border-[#E8DCDD] rounded-sm mb-4 focus:outline-none focus:border-[#8C4A57]"
          data-testid="admin-blog-token-input" />
        <button onClick={login} data-testid="admin-blog-login-btn"
          className="w-full bg-[#8C4A57] text-white py-3 rounded-sm hover:bg-[#6f3945] transition">
          Sign in
        </button>
        <p className="text-xs text-[#6B5E62] mt-4 text-center">
          <Link to="/admin/uploads" className="underline">Back to admin</Link>
        </p>
      </div>
    );
  }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="max-w-6xl mx-auto py-12 px-6" data-testid="admin-blog-page">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl text-[#2D2628]">Blog & Newsletter</h1>
          <p className="text-sm text-[#6B5E62]">Create, edit, and publish posts. Blog and Newsletter share this editor.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/leads" className="text-xs px-3 py-2 border border-[#E8DCDD] rounded-sm hover:border-[#8C4A57] transition text-[#4A3E42]">Leads</Link>
          <Link to="/admin/uploads" className="text-xs px-3 py-2 border border-[#E8DCDD] rounded-sm hover:border-[#8C4A57] transition text-[#4A3E42]">Uploads</Link>
          <button onClick={() => load()} disabled={loading}
            className="flex items-center gap-1.5 text-xs px-3 py-2 border border-[#E8DCDD] rounded-sm hover:border-[#8C4A57] transition text-[#4A3E42]"
            data-testid="admin-blog-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />Refresh
          </button>
          <button onClick={logout} className="flex items-center gap-1.5 text-xs px-3 py-2 text-[#6B5E62] hover:text-[#2D2628]">
            <LogOut className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <aside className="lg:col-span-1 border border-[#E8DCDD] rounded-sm bg-white h-fit sticky top-24" data-testid="admin-blog-list">
          <div className="p-4 border-b border-[#E8DCDD] flex items-center justify-between">
            <span className="text-xs uppercase tracking-widest text-[#6B5E62]">Posts</span>
            <button onClick={newPost} data-testid="admin-blog-new-btn"
              className="flex items-center gap-1 text-xs px-2 py-1 bg-[#8C4A57] text-white rounded-sm hover:bg-[#6f3945]">
              <Plus className="w-3 h-3" />New
            </button>
          </div>
          {["blog", "newsletter"].map((cat) => (
            <div key={cat}>
              <div className="px-4 py-2 bg-[#FAF4F5] text-[10px] uppercase tracking-widest text-[#6B5E62]">
                {cat} ({grouped[cat].length})
              </div>
              {grouped[cat].length === 0 && (
                <div className="px-4 py-3 text-xs text-[#6B5E62]">No posts.</div>
              )}
              {grouped[cat].map((p) => (
                <button key={p.id} onClick={() => editPost(p)}
                  data-testid={`admin-blog-item-${p.slug}`}
                  className={`w-full text-left px-4 py-3 border-t border-[#E8DCDD] hover:bg-[#FAF4F5] ${form.id === p.id ? "bg-[#FAF4F5]" : ""}`}>
                  <div className="text-sm text-[#2D2628] font-semibold truncate">{p.title}</div>
                  <div className="text-[11px] text-[#6B5E62] truncate">/{cat}/{p.slug}</div>
                  <div className="text-[10px] mt-1">
                    <span className={`px-1.5 py-0.5 rounded-full ${p.published ? "bg-[#E8F5EA] text-[#3F7A4F]" : "bg-[#F7E2E5] text-[#8C4A57]"}`}>
                      {p.published ? "Published" : "Draft"}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          ))}
        </aside>

        <section className="lg:col-span-2 border border-[#E8DCDD] rounded-sm bg-white p-6 space-y-4" data-testid="admin-blog-editor">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-xl text-[#2D2628]">{form.id ? "Edit post" : "New post"}</h2>
            {form.id && (
              <button onClick={() => remove(form)} data-testid="admin-blog-delete-btn"
                className="flex items-center gap-1 text-xs px-2 py-1 border border-[#E8DCDD] rounded-sm text-[#8C4A57] hover:border-[#8C4A57]">
                <Trash2 className="w-3 h-3" />Delete
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Category">
              <select value={form.category} onChange={set("category")} data-testid="admin-blog-category"
                className="w-full px-3 py-2 border border-[#E8DCDD] rounded-sm bg-white text-sm">
                <option value="blog">Blog</option>
                <option value="newsletter">Newsletter</option>
              </select>
            </Field>
            <Field label="Slug (URL)">
              <input value={form.slug} onChange={set("slug")} placeholder="my-post-slug"
                data-testid="admin-blog-slug" className={input} />
            </Field>
          </div>

          <Field label="Title">
            <input value={form.title} onChange={set("title")} data-testid="admin-blog-title" className={input} />
          </Field>
          <Field label="Meta title (SEO)">
            <input value={form.meta_title} onChange={set("meta_title")} placeholder="Defaults to title if blank" className={input} />
          </Field>
          <Field label="Subtitle / dek">
            <input value={form.subtitle} onChange={set("subtitle")} className={input} />
          </Field>
          <Field label="Description / meta description">
            <textarea value={form.description} onChange={set("description")} rows={2} className={input} />
          </Field>
          <Field label="Excerpt (card preview)">
            <textarea value={form.excerpt} onChange={set("excerpt")} rows={2} className={input} />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Hero image URL">
              <input value={form.image} onChange={set("image")} className={input} />
            </Field>
            <Field label="Hero image alt">
              <input value={form.image_alt} onChange={set("image_alt")} className={input} />
            </Field>
            <Field label="Target keyword">
              <input value={form.target_keyword} onChange={set("target_keyword")} className={input} />
            </Field>
            <Field label="Publish date">
              <input type="date" value={form.published_at || ""} onChange={set("published_at")}
                data-testid="admin-blog-published-at" className={input} />
            </Field>
            <Field label="Canonical URL (optional)">
              <input value={form.canonical_url} onChange={set("canonical_url")} className={input} />
            </Field>
          </div>

          <Field label="Body HTML">
            <textarea value={form.body_html} onChange={set("body_html")} rows={16}
              data-testid="admin-blog-body" className={`${input} font-mono text-xs`} />
          </Field>

          <label className="flex items-center gap-2 text-sm text-[#2D2628]">
            <input type="checkbox" checked={form.published}
              onChange={(e) => setForm({ ...form, published: e.target.checked })}
              data-testid="admin-blog-published" />
            Published (visible on site)
          </label>

          <button onClick={save} disabled={saving} data-testid="admin-blog-save-btn"
            className="w-full bg-[#8C4A57] text-white py-3 rounded-sm hover:bg-[#6f3945] disabled:opacity-60 uppercase text-sm tracking-widest font-semibold">
            {saving ? "Saving…" : form.id ? "Save changes" : "Create post"}
          </button>

          {form.id && form.category === "newsletter" && form.published && (
            <div className="border-t border-[#E8DCDD] pt-4 mt-4 space-y-2" data-testid="admin-newsletter-send-block">
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57]">Send to subscribers</div>
              <p className="text-xs text-[#6B5E62]">
                Emails every active subscriber via Resend using the post's title, excerpt, and hero image.
                Fires once per click — verify content is final before sending.
              </p>
              <button onClick={sendNewsletter} disabled={sending} data-testid="admin-newsletter-send-btn"
                className="w-full border border-[#8C4A57] text-[#8C4A57] py-2.5 rounded-sm hover:bg-[#FAF4F5] disabled:opacity-60 uppercase text-xs tracking-widest font-semibold">
                {sending ? "Sending…" : "Send this dispatch to all subscribers"}
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

const input = "w-full px-3 py-2 border border-[#E8DCDD] rounded-sm text-sm focus:outline-none focus:border-[#8C4A57]";
const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-widest text-[#6B5E62] mb-1">{label}</label>
    {children}
  </div>
);
